import React, { useMemo, useState } from 'react'
import { Head } from '@inertiajs/react'
import { Stethoscope, ShieldCheck, Lock, Check, ChevronLeft, FileText, Image as ImageIcon, Trash2, Loader2 } from 'lucide-react'

// PUBLIC patient finance application at /finance/apply/:token (Paul 2026-09-30). Bare application layout,
// no dashboard chrome. PHI-minimised: the server sends the first name only, and never the saved ID number.
// Every monthly figure sits beside its number of payments, total and rate (NCA Regulation 21(3)).
// Submits use plain fetch (the intake-409 lesson), not the Inertia router.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const money = (v) => 'R' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100
const kb = (n) => (n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB')

// Short-term credit (NCA: up to R8,000 over up to 6 months) is priced PER MONTH, everything else per year.
const isShortTerm = (amount, months, p) => !!p.short_term && amount <= p.short_term.max_principal && months <= p.short_term.max_months

// MUST match FinanceProgram.quote (Ruby) and PATIENT_FINANCE_KB/sources/calc.py to the cent.
// p.service_fee arrives already VAT-adjusted from the server; the initiation fee is adjusted here.
function quote(amount, months, p) {
  const short = isShortTerm(amount, months, p)
  const rate = short ? p.short_term.monthly_rate * 12 : p.rate
  const feeExcl = Math.min(p.fee_base + p.fee_percent * Math.max(amount - 1000, 0), p.fee_cap)
  const fee = r2(feeExcl * (p.fees_include_vat ? 1 : 1 + p.vat_rate))
  const i = rate / 12
  const principal = amount + fee
  const base = i === 0 ? principal / months : principal * i / (1 - Math.pow(1 + i, -months))
  const instalment = r2(base + p.service_fee)
  const total = r2(instalment * months)
  return { months, instalment, total, fee, cost: r2(total - amount), short }
}

const rateWords = (q, p) => (q.short ? `${p.short_term.rate_label} a month (short-term credit)` : `${p.rate_label} a year, fixed`)

function validId(id) {
  if (!/^\d{13}$/.test(id)) return false
  const mm = +id.slice(2, 4), dd = +id.slice(4, 6)
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || +id[10] > 1) return false
  let sum = 0
  for (let i = 0; i < 13; i++) { let d = +id[12 - i]; if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9 } sum += d }
  return sum % 10 === 0
}
const validCell = (v) => /^(\+27|0)[6-8]\d{8}$/.test(String(v || '').replace(/[\s-]/g, ''))
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ''))

async function send(path, { method = 'POST', json, form } = {}) {
  const csrf = (typeof document !== 'undefined' && document.querySelector('meta[name="csrf-token"]')?.content) || ''
  const headers = { Accept: 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' }
  let body
  if (form) body = form
  else if (json !== undefined) { headers['Content-Type'] = 'application/json'; body = JSON.stringify(json) }
  try {
    const res = await fetch(path, { method, headers, body, credentials: 'same-origin' })
    const data = await res.json().catch(() => ({}))
    return { ok: res.ok && data.ok !== false, status: res.status, data }
  } catch (e) {
    return { ok: false, status: 0, data: { error: 'We couldn’t reach the practice. Check your connection and try again.' } }
  }
}

const EXPENSES = [
  ['exp_housing', 'Rent or bond'], ['exp_transport', 'Transport'], ['exp_food', 'Food and household'],
  ['exp_school', 'School and childcare'], ['exp_insurance', 'Insurance and medical aid'], ['exp_maintenance', 'Maintenance you pay'],
  ['exp_other', 'Anything else'],
]

const card = 'rounded-2xl border border-brand-border bg-white p-5 shadow-sm'
const btnPrimary = 'inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50'
const inputCls = (bad) => `w-full rounded-lg border ${bad ? 'border-red-500' : 'border-brand-border'} bg-white px-3 py-2.5 text-base text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20`

function Field({ id, label, hint, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-brand-ink">{label}</label>
      {children}
      {hint && <span className="text-xs text-brand-muted">{hint}</span>}
      {error && <span className="text-xs font-medium text-red-600">{error}</span>}
    </div>
  )
}

function MoneyInput({ id, value, onChange, bad }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted">R</span>
      <input id={id} type="number" inputMode="numeric" min="0" step="100" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
             className={inputCls(bad) + ' pl-7'} />
    </div>
  )
}

function YesNo({ name, legend, value, onChange, error }) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1 text-sm font-semibold text-brand-ink">{legend}</legend>
      <div className="flex gap-2">
        {['No', 'Yes'].map((v) => (
          <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm ${value === v ? 'border-brand-primary bg-brand-accent' : 'border-brand-border bg-white'}`}>
            <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} className="accent-brand-primary" /> {v}
          </label>
        ))}
      </div>
      {error && <span className="text-xs font-medium text-red-600">{error}</span>}
    </fieldset>
  )
}

function Progress({ n }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="h-1.5 overflow-hidden rounded-full bg-brand-border"><div className="h-full rounded-full bg-brand-primary" style={{ width: `${n * 20}%` }} /></div>
      <span className="text-xs font-medium text-brand-muted">Step {n} of 5{n === 1 ? ' · about 10 minutes in total' : ''}</span>
    </div>
  )
}

export default function PublicFinanceApply(props) {
  const { token, practice = {}, first_name, estimate = {}, lender = {}, pricing = {}, disclosure, optional_note, unconfirmed_note,
          choice_note, statement_months = [], limits = {}, draft, submitted } = props
  const total = Number(estimate.total_value || 0)
  const terms = pricing.terms || [3, 6, 9, 12, 24, 36]

  const [step, setStep] = useState(submitted ? 'submitted' : 'estimate')
  const [pay, setPay] = useState(draft ? 'loan' : null)
  const [term, setTerm] = useState(draft?.term || 12)
  const [own, setOwn] = useState(draft?.own_contribution ? String(draft.own_contribution) : '0')
  const [idHint, setIdHint] = useState(draft?.id_saved_hint || null)
  const [a, setA] = useState(() => ({
    language: 'English', dependants: '0', other_income: '0', debt_repayments: '',
    ...Object.fromEntries(EXPENSES.map(([k]) => [k, '0'])),
    ...(draft?.answers || {}),
  }))
  const [docs, setDocs] = useState(draft?.documents || [])
  const [months, setMonths] = useState(() => statement_months.map(() => !!draft?.answers?.statement_months_confirmed))
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [banner, setBanner] = useState(null)
  const [uploading, setUploading] = useState({})
  const [consent, setConsent] = useState({ share: false, bureau: false, truth: false, marketing: false })
  const [problems, setProblems] = useState([])

  const ownNum = Math.max(0, Number(own) || 0)
  const financed = r2(total - ownNum)
  const loanOk = financed >= (pricing.min_amount || 0) && financed <= (pricing.max_amount || Infinity)
  const rows = useMemo(() => terms.map((m) => quote(total, m, pricing)), [total])
  const q = loanOk ? quote(financed, term, pricing) : null
  const set = (k, v) => setA((prev) => ({ ...prev, [k]: v }))
  const go = (s) => { setStep(s); setErrors({}); setBanner(null); if (typeof window !== 'undefined') window.scrollTo(0, 0) }
  const noPayslip = ['Self-employed', 'Pensioner'].includes(a.employment)

  async function save(nextStep) {
    setBusy(true)
    const answers = { ...a, statement_months_confirmed: months.length > 0 && months.every(Boolean) }
    const r = await send(`/finance/apply/${token}/draft`, { json: { answers, term, own_contribution: ownNum } })
    setBusy(false)
    if (!r.ok) { setBanner(r.data.error || 'We couldn’t save that. Please try again.'); return false }
    if (r.data.id_saved_hint) setIdHint(r.data.id_saved_hint)
    if (a.id_number) set('id_number', '')
    if (nextStep) go(nextStep)
    return true
  }

  function check(fields) {
    const e = {}
    fields.forEach(([k, test, msg]) => { if (!test(a[k])) e[k] = msg })
    setErrors(e)
    if (Object.keys(e).length) { setBanner('Please check the fields marked in red.'); return false }
    return true
  }
  const filled = (v) => String(v ?? '').trim().length > 0

  function next1() {
    const idOk = (v) => (filled(v) ? validId(String(v).trim()) : !!idHint)
    if (check([
      ['first_names', filled, 'Please fill this in.'], ['surname', filled, 'Please fill this in.'],
      ['id_number', idOk, 'That doesn’t look like a valid SA ID number. Check the 13 digits.'],
      ['cell', validCell, 'Enter a South African cellphone number, like 082 123 4567.'],
      ['email', validEmail, 'Check the email address.'],
      ['address', filled, 'Please fill this in.'], ['suburb', filled, 'Please fill this in.'], ['city', filled, 'Please fill this in.'],
      ['postal_code', (v) => /^\d{4}$/.test(String(v || '')), 'Postal codes have 4 digits.'],
      ['years_at_address', filled, 'Please choose one.'], ['marital_status', filled, 'Please choose one.'],
    ])) save(2)
  }
  function next2() {
    const pens = a.employment === 'Pensioner'
    if (check([
      ['employment', filled, 'Please choose one.'],
      ['employer', (v) => pens || filled(v), 'Please fill this in.'], ['occupation', (v) => pens || filled(v), 'Please fill this in.'],
      ['pay_day', filled, 'Please choose one.'],
      ['gross_income', (v) => Number(v) > 0, 'Enter your gross monthly income.'],
      ['net_income', (v) => Number(v) > 0 && Number(v) <= Number(a.gross_income || 0), 'Net income is usually less than gross income.'],
      ['other_income_source', (v) => !(Number(a.other_income) > 0) || filled(v), 'Say where the other income comes from.'],
    ])) save(3)
  }
  function next3() {
    if (check([
      ['debt_repayments', (v) => filled(v) && Number(v) >= 0, 'Enter your monthly repayments, or 0.'],
      ['debt_review', (v) => v === 'Yes' || v === 'No', 'Please choose one.'],
      ['administration', (v) => v === 'Yes' || v === 'No', 'Please choose one.'],
    ])) save(4)
  }

  // ── documents ──
  const SLOTS = [
    { kind: 'id', title: 'ID document', hint: 'Smart card, both sides, or the photo page of your green ID book.', req: true },
    { kind: 'statement', title: `Bank statements, last ${statement_months.length || 3} months`, hint: 'One PDF covering all the months is fine. Tick the months below.', req: true, months: true },
    { kind: 'payslip', title: a.employment === 'Self-employed' ? 'Latest financial statements' : a.employment === 'Pensioner' ? 'Proof of pension' : 'Latest payslip',
      hint: noPayslip ? 'Optional, but it can help Expo Finance decide faster.' : 'Your most recent payslip. If your income changes month to month, add the last three.', req: !noPayslip },
    { kind: 'address', title: 'Proof of address', hint: `Only if ${lender.name || 'the lender'} asks for it. A utility bill or bank letter from the last 3 months.`, req: false },
  ]
  const docsOf = (k) => docs.filter((d) => d.kind === k)
  const slotDone = (s) => {
    const l = docsOf(s.kind)
    if (s.req && !l.length) return false
    if (l.some((d) => d.locked && !d.has_password)) return false
    if (s.months && l.length && !months.every(Boolean)) return false
    return true
  }
  async function upload(kind, fileList) {
    const files = Array.from(fileList || [])
    for (const f of files) {
      if (f.size > (limits.max_file_mb || 10) * 1048576) { setBanner(`${f.name} is larger than ${limits.max_file_mb || 10} MB.`); continue }
      setUploading((u) => ({ ...u, [kind]: (u[kind] || 0) + 1 }))
      const form = new FormData(); form.append('kind', kind); form.append('file', f)
      const r = await send(`/finance/apply/${token}/documents`, { form })
      setUploading((u) => ({ ...u, [kind]: Math.max(0, (u[kind] || 1) - 1) }))
      if (r.ok) setDocs((d) => [...d, r.data.document])
      else setBanner(r.data.error || `We couldn’t add ${f.name}.`)
    }
  }
  async function removeDoc(doc) {
    const r = await send(`/finance/apply/${token}/documents/${doc.id}`, { method: 'DELETE' })
    if (r.ok) setDocs((d) => d.filter((x) => x.id !== doc.id))
    else setBanner(r.data.error || 'We couldn’t remove that file.')
  }
  async function savePassword(doc, pw) {
    const r = await send(`/finance/apply/${token}/documents/${doc.id}`, { method: 'PATCH', json: { password: pw } })
    if (r.ok) setDocs((d) => d.map((x) => (x.id === doc.id ? r.data.document : x)))
  }
  function next4() {
    const missing = SLOTS.filter((s) => !slotDone(s)).map((s) => {
      const l = docsOf(s.kind)
      if (l.some((d) => d.locked && !d.has_password)) return `${s.title} (password needed)`
      if (s.months && l.length && !months.every(Boolean)) return `${s.title} (tick all the months)`
      return s.title
    })
    if (missing.length) { setBanner('Still needed: ' + missing.join(', ') + '.'); return }
    save(5)
  }

  async function submit() {
    setBusy(true); setProblems([])
    const saved = await save(null)
    if (!saved) { setBusy(false); return }
    const r = await send(`/finance/apply/${token}/submit`, { json: {
      consent_share: consent.share, consent_bureau: consent.bureau, consent_truth: consent.truth, consent_marketing: consent.marketing,
    } })
    setBusy(false)
    if (r.ok && r.data.status_url) { window.location.href = r.data.status_url; return }
    if (r.data.problems) setProblems(r.data.problems)
    else setBanner(r.data.error || 'We couldn’t send your application. Please try again.')
  }

  const header = (
    <div className={card}>
      <div className="flex items-center gap-2 text-brand-primary"><Stethoscope size={18} /><span className="text-sm font-semibold">{practice.name || 'Dr Chalita le Roux'}</span></div>
      <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-brand-primary">Spread the cost</p>
      <h1 className="mt-1 text-xl font-semibold text-brand-ink">Hi {first_name || 'there'}</h1>
      <p className="mt-1 text-sm text-brand-muted">{estimate.kind === 'plan' ? estimate.number : `Estimate ${estimate.number}`} · total <b className="text-brand-ink">{estimate.total}</b></p>
    </div>
  )
  const back = (to) => (
    <button type="button" onClick={() => go(to)} className="inline-flex items-center gap-1 self-start text-sm font-semibold text-brand-primary"><ChevronLeft size={16} /> Back</button>
  )
  const bannerEl = banner && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{banner}</div>

  return (
    <div className="min-h-screen bg-brand-surface/40 px-4 py-6">
      <Head title={`Spread the cost — ${practice.name || 'Dr Chalita le Roux'}`} />
      <div className="mx-auto flex max-w-xl flex-col gap-5">
        {header}

        {step === 'submitted' && (
          <div className={card}>
            <h2 className="text-lg font-semibold text-brand-ink">You’ve already applied</h2>
            <p className="mt-1 text-sm text-brand-muted">Your application {submitted.reference} is with {lender.name}. You can follow it here.</p>
            <a href={submitted.status_url} className={btnPrimary + ' mt-4'}>See my application</a>
          </div>
        )}

        {step === 'estimate' && (
          <div className={card + ' flex flex-col gap-4'}>
            <h2 className="text-lg font-semibold text-brand-ink">Your monthly options</h2>
            {choice_note && <p className="-mt-2 text-sm text-brand-muted">{choice_note}</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted"><th className="py-2 pr-2">Term</th><th className="py-2 pr-2 text-right">Monthly</th><th className="py-2 text-right">Total</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.months} className="border-b border-brand-border/60"><th className="py-2 pr-2 text-left font-semibold text-brand-ink">{r.months} months{r.short ? ' *' : ''}</th><td className="py-2 pr-2 text-right tabular-nums">{money(r.instalment)}</td><td className="py-2 text-right tabular-nums">{money(r.total)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.some((r) => r.short) && <p className="-mt-2 text-xs text-brand-muted">* Short-term credit, priced per month.</p>}
            <p className="text-xs leading-relaxed text-brand-muted">{disclosure}</p>
            {unconfirmed_note && <p className="text-xs leading-relaxed text-amber-700">{unconfirmed_note}</p>}
            <p className="text-xs leading-relaxed text-brand-muted">{optional_note}</p>
            <button type="button" className={btnPrimary} onClick={() => go('options')}>See my payment options</button>
          </div>
        )}

        {step === 'options' && (
          <div className="flex flex-col gap-4">
            {back('estimate')}
            <h2 className="text-lg font-semibold text-brand-ink">How would you like to pay?</h2>
            <p className="-mt-2 text-sm text-brand-muted">Your treatment plan and fees are the same however you pay.</p>
            {[['full', 'Pay in full at your visit', estimate.total, 'By card or EFT on the day of your treatment.'],
              ['loan', `Monthly with ${lender.name}`, `${terms[0]} to ${terms[terms.length - 1]} months`, `A loan from ${lender.legal_name}, a registered credit provider (${lender.ncrcp}). Subject to its assessment.`]].map(([v, title, amt, note]) => (
              <div key={v} className={`rounded-2xl border-2 bg-white p-4 shadow-sm ${pay === v ? 'border-brand-primary' : 'border-brand-border'}`}>
                <label className="flex cursor-pointer items-start gap-3">
                  <input type="radio" name="pay" checked={pay === v} onChange={() => setPay(v)} className="mt-1 accent-brand-primary" />
                  <span className="flex-1"><span className="flex flex-wrap items-baseline justify-between gap-2"><b className="text-brand-ink">{title}</b><span className="font-semibold tabular-nums text-brand-ink">{amt}</span></span>
                    <span className="mt-1 block text-sm text-brand-muted">{note}</span></span>
                </label>
                {v === 'loan' && pay === 'loan' && (
                  <div className="mt-4 flex flex-col gap-4 border-t border-brand-border pt-4">
                    <div>
                      <span className="text-sm font-semibold text-brand-ink">How many months?</span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {terms.map((m) => (
                          <button key={m} type="button" onClick={() => setTerm(m)} aria-pressed={term === m}
                                  className={`rounded-full border px-4 py-2 text-sm font-semibold tabular-nums ${term === m ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink'}`}>{m} months</button>
                        ))}
                      </div>
                    </div>
                    {q ? (
                      <div className="rounded-xl bg-brand-surface px-4 py-3 text-sm text-brand-muted">
                        <div className="text-xl font-bold tabular-nums text-brand-ink">{money(q.instalment)} a month</div>
                        <div>{term} payments. Total repayable {money(q.total)}, at {rateWords(q, pricing)}.</div>
                        <div>Includes a {money(q.fee)} initiation fee added to the loan and a {money(pricing.service_fee)} monthly service fee. No final or balloon payment.{ownNum > 0 ? ` You pay ${money(ownNum)} yourself at your first visit.` : ''}</div>
                        <div>Subject to {lender.name}’s assessment. This is not a quotation.</div>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">{lender.name} lends from {money(pricing.min_amount)}. Lower the amount you pay yourself, or pay in full.</div>
                    )}
                    <Field id="own" label="Pay part yourself at your first visit (optional)">
                      <MoneyInput id="own" value={own} onChange={setOwn} />
                    </Field>
                  </div>
                )}
              </div>
            ))}
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={!pay || (pay === 'loan' && !q) || busy}
                    onClick={() => (pay === 'full' ? go('full') : save(1))}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Continue
            </button>
            <p className="text-xs text-brand-muted">{optional_note} {lender.name}, not the practice, decides on loans and sets the final rate and fees.</p>
          </div>
        )}

        {step === 'full' && (
          <div className="flex flex-col gap-4">
            {back('options')}
            <div className={card}>
              <h2 className="text-lg font-semibold text-brand-ink">You’re all set</h2>
              <p className="mt-1 text-sm text-brand-muted">You’ll pay <b className="text-brand-ink">{estimate.total}</b> at your visit, by card or EFT. There’s nothing else to fill in.</p>
              <a className={btnPrimary + ' mt-4'} target="_blank" rel="noopener noreferrer"
                 href={`https://wa.me/${String(practice.wa || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hi, I'd like to book my treatment (${estimate.book_ref || estimate.number}).`)}`}>Book my visit on WhatsApp</a>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-4">
            {back('options')}<Progress n={1} />
            <h2 className="text-lg font-semibold text-brand-ink">About you</h2>
            <p className="-mt-2 text-sm text-brand-muted">{lender.name} needs this to check who you are.</p>
            <div className={card + ' flex flex-col gap-4'}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="first_names" label="First names" error={errors.first_names}><input id="first_names" className={inputCls(errors.first_names)} value={a.first_names || ''} onChange={(e) => set('first_names', e.target.value)} autoComplete="given-name" /></Field>
                <Field id="surname" label="Surname" error={errors.surname}><input id="surname" className={inputCls(errors.surname)} value={a.surname || ''} onChange={(e) => set('surname', e.target.value)} autoComplete="family-name" /></Field>
              </div>
              <Field id="id_number" label="South African ID number" error={errors.id_number}
                     hint={idHint && !a.id_number ? `Saved, ending in ${idHint}. Type it again only to change it.` : 'Using a passport? Reception will help you.'}>
                <input id="id_number" className={inputCls(errors.id_number)} inputMode="numeric" maxLength={13} autoComplete="off" value={a.id_number || ''}
                       onChange={(e) => set('id_number', e.target.value.replace(/\D/g, '').slice(0, 13))} placeholder="13 digits" />
              </Field>
              <Field id="cell" label="Cellphone" error={errors.cell}><input id="cell" type="tel" className={inputCls(errors.cell)} value={a.cell || ''} onChange={(e) => set('cell', e.target.value)} autoComplete="tel" placeholder="082 123 4567" /></Field>
              <Field id="email" label="Email" error={errors.email}><input id="email" type="email" className={inputCls(errors.email)} value={a.email || ''} onChange={(e) => set('email', e.target.value)} autoComplete="email" /></Field>
            </div>
            <div className={card + ' flex flex-col gap-4'}>
              <h3 className="font-semibold text-brand-ink">Where you live</h3>
              <Field id="address" label="Street address" error={errors.address}><input id="address" className={inputCls(errors.address)} value={a.address || ''} onChange={(e) => set('address', e.target.value)} autoComplete="address-line1" /></Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="suburb" label="Suburb" error={errors.suburb}><input id="suburb" className={inputCls(errors.suburb)} value={a.suburb || ''} onChange={(e) => set('suburb', e.target.value)} /></Field>
                <Field id="city" label="City or town" error={errors.city}><input id="city" className={inputCls(errors.city)} value={a.city || ''} onChange={(e) => set('city', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="postal_code" label="Postal code" error={errors.postal_code}><input id="postal_code" inputMode="numeric" maxLength={4} className={inputCls(errors.postal_code)} value={a.postal_code || ''} onChange={(e) => set('postal_code', e.target.value.replace(/\D/g, '').slice(0, 4))} autoComplete="postal-code" /></Field>
                <Field id="years_at_address" label="Years at this address" error={errors.years_at_address}>
                  <select id="years_at_address" className={inputCls(errors.years_at_address)} value={a.years_at_address || ''} onChange={(e) => set('years_at_address', e.target.value)}>
                    <option value="">Choose</option>{['Less than 1 year', '1 to 2 years', '3 to 5 years', 'More than 5 years'].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <div className={card + ' flex flex-col gap-4'}>
              <h3 className="font-semibold text-brand-ink">Your household</h3>
              <Field id="marital_status" label="Marital status" error={errors.marital_status}
                     hint={a.marital_status === 'Married in community of property' ? `${lender.name} may ask for your spouse’s details, because you are married in community of property.` : null}>
                <select id="marital_status" className={inputCls(errors.marital_status)} value={a.marital_status || ''} onChange={(e) => set('marital_status', e.target.value)}>
                  <option value="">Choose</option>{['Single', 'Married in community of property', 'Married out of community of property', 'Living together', 'Divorced', 'Widowed'].map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field id="dependants" label="People who depend on you financially"><input id="dependants" type="number" min="0" max="20" className={inputCls(false)} value={a.dependants || '0'} onChange={(e) => set('dependants', e.target.value)} /></Field>
              <fieldset className="flex flex-col gap-1.5"><legend className="mb-1 text-sm font-semibold text-brand-ink">Language for your loan documents</legend>
                <div className="flex gap-2">{['English', 'Afrikaans'].map((v) => (
                  <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm ${a.language === v ? 'border-brand-primary bg-brand-accent' : 'border-brand-border bg-white'}`}>
                    <input type="radio" name="language" checked={a.language === v} onChange={() => set('language', v)} className="accent-brand-primary" /> {v}</label>))}</div>
              </fieldset>
            </div>
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={busy} onClick={next1}>Continue</button>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            {back(1)}<Progress n={2} />
            <h2 className="text-lg font-semibold text-brand-ink">Work and income</h2>
            <p className="-mt-2 text-sm text-brand-muted">{lender.name} checks this against your payslip and bank statements.</p>
            <div className={card + ' flex flex-col gap-4'}>
              <Field id="employment" label="How are you employed?" error={errors.employment}>
                <select id="employment" className={inputCls(errors.employment)} value={a.employment || ''} onChange={(e) => set('employment', e.target.value)}>
                  <option value="">Choose</option>{['Permanent', 'Contract', 'Self-employed', 'Pensioner', 'Other'].map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              {a.employment !== 'Pensioner' && (<>
                <Field id="employer" label={a.employment === 'Self-employed' ? 'Business name' : 'Employer'} error={errors.employer}><input id="employer" className={inputCls(errors.employer)} value={a.employer || ''} onChange={(e) => set('employer', e.target.value)} autoComplete="organization" /></Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field id="occupation" label="Occupation" error={errors.occupation}><input id="occupation" className={inputCls(errors.occupation)} value={a.occupation || ''} onChange={(e) => set('occupation', e.target.value)} /></Field>
                  <Field id="employed_since" label="Working there since"><input id="employed_since" type="month" className={inputCls(false)} value={a.employed_since || ''} onChange={(e) => set('employed_since', e.target.value)} /></Field>
                </div>
              </>)}
              <Field id="pay_day" label="Pay day" error={errors.pay_day}>
                <select id="pay_day" className={inputCls(errors.pay_day)} value={a.pay_day || ''} onChange={(e) => set('pay_day', e.target.value)}>
                  <option value="">Choose</option>{Array.from({ length: 31 }, (_, i) => String(i + 1)).concat(['Last working day']).map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="gross_income" label="Gross monthly income" hint="Before tax and deductions" error={errors.gross_income}><MoneyInput id="gross_income" value={a.gross_income} onChange={(v) => set('gross_income', v)} bad={errors.gross_income} /></Field>
                <Field id="net_income" label="Net monthly income" hint="What reaches your bank" error={errors.net_income}><MoneyInput id="net_income" value={a.net_income} onChange={(v) => set('net_income', v)} bad={errors.net_income} /></Field>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="other_income" label="Other income a month (optional)"><MoneyInput id="other_income" value={a.other_income} onChange={(v) => set('other_income', v)} /></Field>
                <Field id="other_income_source" label="From what?" error={errors.other_income_source}><input id="other_income_source" className={inputCls(errors.other_income_source)} value={a.other_income_source || ''} onChange={(e) => set('other_income_source', e.target.value)} placeholder="For example rental" /></Field>
              </div>
            </div>
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={busy} onClick={next2}>Continue</button>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-4">
            {back(2)}<Progress n={3} />
            <h2 className="text-lg font-semibold text-brand-ink">Monthly expenses and debts</h2>
            <p className="-mt-2 text-sm text-brand-muted">Rough amounts are fine. Leave anything that doesn’t apply at zero.</p>
            <div className={card + ' grid grid-cols-1 gap-4 sm:grid-cols-2'}>
              {EXPENSES.map(([k, label]) => (
                <Field key={k} id={k} label={label}><MoneyInput id={k} value={a[k]} onChange={(v) => set(k, v)} /></Field>
              ))}
            </div>
            <div className={card + ' flex flex-col gap-4'}>
              <Field id="debt_repayments" label="Total monthly repayments on loans, cards and accounts" hint={`${lender.name} also checks this with a credit bureau.`} error={errors.debt_repayments}>
                <MoneyInput id="debt_repayments" value={a.debt_repayments} onChange={(v) => set('debt_repayments', v)} bad={errors.debt_repayments} />
              </Field>
              <YesNo name="debt_review" legend="Are you under debt review?" value={a.debt_review} onChange={(v) => set('debt_review', v)} error={errors.debt_review} />
              <YesNo name="administration" legend="Are you under administration, or sequestrated?" value={a.administration} onChange={(v) => set('administration', v)} error={errors.administration} />
              {(a.debt_review === 'Yes' || a.administration === 'Yes') && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{lender.name} can’t usually lend while you’re under debt review, administration or sequestration. You can still pay in full, or ask reception about spreading your treatment over more visits.</p>
              )}
            </div>
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={busy} onClick={next3}>Continue</button>
          </div>
        )}

        {step === 4 && (
          <div className="flex flex-col gap-4">
            {back(3)}<Progress n={4} />
            <h2 className="text-lg font-semibold text-brand-ink">Your documents</h2>
            <p className="-mt-2 text-sm text-brand-muted">Photos from your phone or the PDFs from your banking app are both fine.</p>
            <div className="flex gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs leading-relaxed text-emerald-800">
              <ShieldCheck size={16} className="mt-0.5 flex-shrink-0" />
              <span>Your files are encrypted and go to {lender.name} only. We delete them from our system 30 days after {lender.name}’s decision.</span>
            </div>
            {SLOTS.map((s) => {
              const list = docsOf(s.kind)
              const done = list.length > 0 && slotDone(s)
              const max = (limits.max_files || {})[s.kind] || 3
              return (
                <div key={s.kind} className={`flex flex-col gap-3 rounded-2xl border-2 bg-white p-4 ${done ? 'border-emerald-300' : 'border-dashed border-brand-border'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><b className="text-brand-ink">{s.title}</b><p className="mt-0.5 text-xs text-brand-muted">{s.hint}</p></div>
                    <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${done ? 'bg-emerald-50 text-emerald-700' : s.req ? 'bg-amber-50 text-amber-800' : 'bg-brand-surface text-brand-muted'}`}>{done ? 'Added' : s.req ? 'Needed' : 'Optional'}</span>
                  </div>
                  {list.map((d) => (
                    <div key={d.id} className="flex flex-col gap-2 rounded-lg bg-brand-surface/70 p-2.5">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-accent text-brand-primary">{d.image ? <ImageIcon size={18} /> : <FileText size={18} />}</span>
                        <span className="min-w-0 flex-1"><span className="block break-all text-sm font-semibold text-brand-ink">{d.name}</span>
                          <span className="text-xs text-brand-muted">{kb(d.size)}{d.pages ? ` · ${d.pages} page${d.pages > 1 ? 's' : ''}` : ''}{d.locked ? (d.has_password ? ' · locked, password added' : ' · locked') : ''}</span></span>
                        <button type="button" onClick={() => removeDoc(d)} className="rounded p-2 text-red-600 hover:bg-red-50" aria-label={`Remove ${d.name}`}><Trash2 size={16} /></button>
                      </div>
                      {d.locked && (
                        <div className="flex flex-col gap-1.5 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800">
                          <span className="flex items-center gap-1.5"><Lock size={13} /> This PDF is locked with a password. Many banks use your ID number. Enter it so {lender.name} can open the file.</span>
                          <input type="password" autoComplete="off" placeholder="PDF password" defaultValue="" onBlur={(e) => e.target.value && savePassword(d, e.target.value)}
                                 className={inputCls(false)} aria-label={`Password for ${d.name}`} />
                        </div>
                      )}
                    </div>
                  ))}
                  {s.months && (
                    <div><span className="text-sm font-semibold text-brand-ink">My statements cover</span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {statement_months.map((m, i) => (
                          <button key={m} type="button" aria-pressed={months[i]} onClick={() => setMonths((ms) => ms.map((x, j) => (j === i ? !x : x)))}
                                  className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold ${months[i] ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink'}`}>{m}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {list.length < max && (
                    <label className="relative inline-flex cursor-pointer items-center justify-center gap-2 self-start rounded-lg border-2 border-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary">
                      {uploading[s.kind] ? <><Loader2 size={15} className="animate-spin" /> Adding…</> : (list.length ? 'Add another' : 'Choose file or take a photo')}
                      <input type="file" accept="application/pdf,image/*" multiple={max > 1} className="absolute inset-0 cursor-pointer opacity-0"
                             onChange={(e) => { upload(s.kind, e.target.files); e.target.value = '' }} />
                    </label>
                  )}
                </div>
              )
            })}
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={busy || Object.values(uploading).some(Boolean)} onClick={next4}>Continue</button>
          </div>
        )}

        {step === 5 && (
          <div className="flex flex-col gap-4">
            {back(4)}<Progress n={5} />
            <h2 className="text-lg font-semibold text-brand-ink">Check and send</h2>
            <div className={card + ' flex flex-col gap-2 text-sm'}>
              <div className="flex justify-between gap-3"><span className="text-brand-muted">Treatment</span><span className="text-right">{estimate.number}, {estimate.total}</span></div>
              {ownNum > 0 && <div className="flex justify-between gap-3"><span className="text-brand-muted">You pay at your first visit</span><span>{money(ownNum)}</span></div>}
              <div className="flex justify-between gap-3"><span className="text-brand-muted">Amount to finance</span><span className="font-semibold">{money(financed)}</span></div>
              {q && <div className="flex justify-between gap-3"><span className="text-brand-muted">Estimated monthly</span><span className="text-right">{money(q.instalment)} for {term} months (total {money(q.total)})</span></div>}
              <div className="flex justify-between gap-3"><span className="text-brand-muted">Documents</span><span>{docs.length} added</span></div>
              <div className="flex justify-between gap-3"><span className="text-brand-muted">Applicant</span><span className="text-right">{[a.first_names, a.surname].filter(Boolean).join(' ')}</span></div>
            </div>
            <div className={card + ' flex flex-col gap-3'}>
              <h3 className="font-semibold text-brand-ink">Your consent</h3>
              {[['share', `I consent to Dr Chalita le Roux Inc sending the information and documents on this page, securely, to ${lender.legal_name} (${lender.ncrcp}), a registered credit provider, so it can assess and verify my finance application, check my credit record, contact me about the outcome and meet its legal duties. ${lender.name} is responsible for this information, and the practice handles it on ${lender.name}’s behalf. The practice deletes its copy 30 days after ${lender.name}’s decision.`, true],
                ['bureau', `I agree that ${lender.name} may check my credit record with a credit bureau.`, true],
                ['truth', 'The information I have given is true and complete.', true],
                ['marketing', `${lender.name} may send me offers. (Optional)`, false]].map(([k, text, req]) => (
                <label key={k} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-brand-ink">
                  <input type="checkbox" checked={consent[k]} onChange={(e) => setConsent((c) => ({ ...c, [k]: e.target.checked }))} className="mt-1 h-4 w-4 flex-shrink-0 accent-brand-primary" />
                  <span>{text}{req ? <span className="text-brand-muted"> (required)</span> : null}</span>
                </label>
              ))}
            </div>
            {problems.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <b>Please fix these first:</b>
                <ul className="mt-1 list-disc pl-5">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
              </div>
            )}
            {bannerEl}
            <button type="button" className={btnPrimary} disabled={busy || !(consent.share && consent.bureau && consent.truth)} onClick={submit}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Send my application to {lender.name}
            </button>
            <p className="text-xs text-brand-muted">{lender.name} sends you its own quotation and credit agreement before you sign anything. You can still pay in full at any time.</p>
          </div>
        )}

        <p className="pb-6 text-center text-xs text-brand-muted">Dr Chalita le Roux Inc · Roodepoort · Loans are provided by {lender.legal_name} ({lender.ncrcp}), not by the practice.</p>
      </div>
    </div>
  )
}
